import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, authHelpers } from '../../lib/supabase';

interface HomeContent {
  id: string;
  hero_title: string;
  hero_subtitle: string;
  testimonials: Array<{
    name: string;
    role: string;
    comment: string;
    rating: number;
    avatar: string;
  }>;
  stats: {
    users: number;
    spaces: number;
    income: number;
    satisfaction: number;
  };
  updated_at: string;
}

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  phone: string;
  is_admin: boolean;
  created_at: string;
}

export default function AdminPanel() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const [isAdmin, setIsAdmin] = useState(false);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);
  
  const [heroData, setHeroData] = useState({
    title: 'Boş alanlarınızı kazanca çevirin',
    subtitle: 'padbaq ile otoparkınızı kiralayın, eşyalarınızı saklayın, kolilerinizi takip edin.'
  });

  const [statsData, setStatsData] = useState({
    users: 15000,
    spaces: 8500,
    income: 2500000,
    satisfaction: 98
  });

  const [testimonials, setTestimonials] = useState([
    {
      name: "Ayşe Yılmaz",
      role: "Daire Sahibi",
      comment: "Kullanmadığım park yerimi kiraya vererek ayda 2000₺ ek gelir elde ediyorum. Çok memnunum!",
      rating: 5,
      avatar: "https://public.readdy.ai/ai/img_res/aa53eb4ae86bd77edd0942bc8251c4fe.jpg"
    }
  ]);

  const [editingTestimonialIndex, setEditingTestimonialIndex] = useState<number | null>(null);

  useEffect(() => {
    checkAdminAndLoad();
  }, [user, navigate]);

  const checkAdminAndLoad = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('Admin kontrolü yapılıyor, user ID:', user.id);
      
      // Admin kontrolü - direkt veritabanından çek
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('is_admin, email')
        .eq('id', user.id)
        .single();

      console.log('Admin kontrol sonucu:', { profileData, profileError, userId: user.id });

      if (profileError) {
        console.error('Profil yüklenirken hata:', profileError);
        
        // Profil yoksa oluştur
        if (profileError.code === 'PGRST116') {
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: user.id,
              email: user.email,
              is_admin: user.email === 'fadiloglu@kalyonkentsel.com',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });

          if (insertError) {
            console.error('Profil oluşturma hatası:', insertError);
            alert('Profil oluşturulamadı. Lütfen tekrar giriş yapın.');
            await signOut();
            navigate('/login');
            return;
          }

          // Profil oluşturulduktan sonra tekrar kontrol et
          const adminStatus = user.email === 'fadiloglu@kalyonkentsel.com';
          console.log('Admin durumu (yeni profil):', adminStatus);
          setIsAdmin(adminStatus);
          
          if (!adminStatus) {
            alert('Bu sayfaya erişim yetkiniz yok.');
            navigate('/dashboard');
            return;
          }
        } else {
          alert('Profil bilgileriniz yüklenemedi. Lütfen tekrar giriş yapın.');
          await signOut();
          navigate('/login');
          return;
        }
      } else {
        const adminStatus = profileData?.is_admin === true;
        console.log('Admin durumu:', adminStatus, 'profileData:', profileData);
        setIsAdmin(adminStatus);
        
        if (!adminStatus) {
          alert('Bu sayfaya erişim yetkiniz yok.');
          navigate('/dashboard');
          return;
        }
      }
      
      await loadContent();
      await loadUsers();
    } catch (err) {
      console.error('Admin kontrol hatası:', err);
      alert('Bir hata oluştu. Lütfen tekrar giriş yapın.');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadContent = async () => {
    try {
      const { data, error } = await supabase
        .from('home_content')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      console.log('İçerik yükleme:', { data, error });

      if (error) {
        console.error('İçerik yükleme hatası:', error);
        // Hata varsa varsayılan değerleri kullan
        return;
      }

      if (data) {
        setHeroData({
          title: data.hero_title || 'Boş alanlarınızı kazanca çevirin',
          subtitle: data.hero_subtitle || 'padbaq ile otoparkınızı kiralayın, eşyalarınızı saklayın, kolilerinizi takip edin.'
        });
        setStatsData(data.stats || {
          users: 15000,
          spaces: 8500,
          income: 2500000,
          satisfaction: 98
        });
        setTestimonials(data.testimonials && data.testimonials.length > 0 ? data.testimonials : [
          {
            name: "Ayşe Yılmaz",
            role: "Daire Sahibi",
            comment: "Kullanmadığım park yerimi kiraya vererek ayda 2000₺ ek gelir elde ediyorum. Çok memnunum!",
            rating: 5,
            avatar: "https://readdy.ai/api/search-image?query=Professional%20Turkish%20woman%20smiling%20portrait%20headshot%20clean%20white%20background%20business%20casual%20attire%20confident%20friendly%20expression%20modern%20photography&width=80&height=80&seq=avatar001&orientation=squarish"
          }
        ]);
      }
    } catch (err) {
      console.error('İçerik yüklenirken hata:', err);
    }
  };

  const loadUsers = async () => {
    try {
      console.log('Kullanıcılar yükleniyor...');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('Kullanıcılar yükleme:', { data, error, count: data?.length });

      if (error) {
        console.error('Kullanıcılar yükleme hatası:', error);
        console.error('Hata detayı:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        return;
      }

      if (data) {
        console.log('Yüklenen kullanıcılar:', data);
        setUsers(data);
      }
    } catch (err) {
      console.error('Kullanıcılar yüklenirken hata:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const contentData = {
        hero_title: heroData.title,
        hero_subtitle: heroData.subtitle,
        stats: statsData,
        testimonials: testimonials,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('home_content')
        .upsert(contentData);

      if (error) {
        alert('Kaydetme sırasında hata oluştu: ' + error.message);
      } else {
        alert('Değişiklikler başarıyla kaydedildi!');
      }
    } catch (err) {
      alert('Bir hata oluştu. Lütfen tekrar deneyin.');
    }
    setSaving(false);
  };

  const toggleUserAdmin = async (userId: string, currentStatus: boolean) => {
    if (userId === user?.id) {
      alert('Kendi admin yetkilerinizi değiştiremezsiniz.');
      return;
    }

    const confirmMessage = currentStatus 
      ? 'Bu kullanıcının admin yetkisini kaldırmak istediğinizden emin misiniz?' 
      : 'Bu kullanıcıya admin yetkisi vermek istediğinizden emin misiniz?';

    if (!confirm(confirmMessage)) {
      return;
    }

    setTogglingUserId(userId);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_admin: !currentStatus, updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (error) {
        console.error('Yetki güncelleme hatası:', error);
        alert('Yetki güncellenirken hata oluştu: ' + error.message);
      } else {
        alert(currentStatus ? 'Admin yetkisi kaldırıldı!' : 'Admin yetkisi verildi!');
        await loadUsers();
      }
    } catch (err) {
      console.error('Yetki güncelleme hatası:', err);
      alert('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setTogglingUserId(null);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const addTestimonial = () => {
    setTestimonials([...testimonials, {
      name: "",
      role: "",
      comment: "",
      rating: 5,
      avatar: "https://readdy.ai/api/search-image?query=Professional%20Turkish%20person%20smiling%20portrait%20headshot%20clean%20white%20background%20business%20casual%20attire%20confident%20friendly%20expression%20modern%20photography&width=80&height=80&seq=avatar" + Date.now() + "&orientation=squarish"
    }]);
    setEditingTestimonialIndex(testimonials.length);
  };

  const removeTestimonial = (index: number) => {
    if (confirm('Bu kullanıcı deneyimini silmek istediğinizden emin misiniz?')) {
      setTestimonials(testimonials.filter((_, i) => i !== index));
    }
  };

  const updateTestimonial = (index: number, field: string, value: any) => {
    const updated = [...testimonials];
    updated[index] = { ...updated[index], [field]: value };
    setTestimonials(updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#121C2E' }}>
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-orange-500 animate-spin"></i>
          <p className="mt-4 text-white/70">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121C2E' }}>
      {/* Header */}
      <header className="relative z-10 bg-white/10 backdrop-blur-md shadow-sm sticky top-0 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-white cursor-pointer">
                pad<span className="text-orange-500">baq</span>
              </Link>
            </div>
            <nav className="flex items-center space-x-4">
              <Link to="/" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-home-line mr-2"></i>
                Ana Sayfa
              </Link>
              <Link to="/dashboard" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Panel</Link>
              <Link to="/admin" className="text-orange-500 font-medium px-4 py-2 rounded-lg bg-white/10 whitespace-nowrap cursor-pointer">
                <i className="ri-shield-star-line mr-2"></i>
                Admin Panel
              </Link>
              <button 
                onClick={handleLogout}
                className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-circle-line mr-2"></i>
                Çıkış
              </button>
            </nav>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Admin Paneli</h2>
          <p className="text-white/70">Ana sayfa içeriklerini ve kullanıcıları buradan yönetebilirsiniz</p>
        </div>

        {/* Tabs */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg border border-white/10 mb-6">
          <div className="flex border-b border-white/10 overflow-x-auto">
            <button
              onClick={() => setActiveTab('hero')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'hero'
                  ? 'text-orange-500 border-b-2 border-orange-500 bg-white/5'
                  : 'text-white/70 hover:text-white hover:bg-white/5'
              }`}
            >
              <i className="ri-home-4-line mr-2"></i>
              Hero Bölümü
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'stats'
                  ? 'text-orange-500 border-b-2 border-orange-500 bg-white/5'
                  : 'text-white/70 hover:text-white hover:bg-white/5'
              }`}
            >
              <i className="ri-bar-chart-line mr-2"></i>
              İstatistikler
            </button>
            <button
              onClick={() => setActiveTab('testimonials')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'testimonials'
                  ? 'text-orange-500 border-b-2 border-orange-500 bg-white/5'
                  : 'text-white/70 hover:text-white hover:bg-white/5'
              }`}
            >
              <i className="ri-chat-quote-line mr-2"></i>
              Kullanıcı Deneyimleri
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'users'
                  ? 'text-orange-500 border-b-2 border-orange-500 bg-white/5'
                  : 'text-white/70 hover:text-white hover:bg-white/5'
              }`}
            >
              <i className="ri-user-settings-line mr-2"></i>
              Kullanıcı Yönetimi
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg border border-white/10 p-8">
          {/* Hero Section Tab */}
          {activeTab === 'hero' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Ana Başlık</label>
                <input
                  type="text"
                  value={heroData.title}
                  onChange={(e) => setHeroData({ ...heroData, title: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Ana başlık"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">Alt Başlık</label>
                <textarea
                  value={heroData.subtitle}
                  onChange={(e) => setHeroData({ ...heroData, subtitle: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  placeholder="Alt başlık"
                />
              </div>
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Aktif Kullanıcı</label>
                <input
                  type="number"
                  value={statsData.users}
                  onChange={(e) => setStatsData({ ...statsData, users: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">Kiralık Alan</label>
                <input
                  type="number"
                  value={statsData.spaces}
                  onChange={(e) => setStatsData({ ...statsData, spaces: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">Aylık Gelir (₺)</label>
                <input
                  type="number"
                  value={statsData.income}
                  onChange={(e) => setStatsData({ ...statsData, income: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">Memnuniyet (%)</label>
                <input
                  type="number"
                  value={statsData.satisfaction}
                  onChange={(e) => setStatsData({ ...statsData, satisfaction: parseInt(e.target.value) || 0 })}
                  min="0"
                  max="100"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          )}

          {/* Testimonials Tab */}
          {activeTab === 'testimonials' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white">Kullanıcı Deneyimleri</h3>
                <button
                  onClick={addTestimonial}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line mr-2"></i>
                  Yeni Ekle
                </button>
              </div>

              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="text-white font-medium">Deneyim #{index + 1}</h4>
                    <button
                      onClick={() => removeTestimonial(index)}
                      className="text-red-400 hover:text-red-300 cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-xl"></i>
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2">İsim</label>
                      <input
                        type="text"
                        value={testimonial.name}
                        onChange={(e) => updateTestimonial(index, 'name', e.target.value)}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="İsim Soyisim"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2">Rol</label>
                      <input
                        type="text"
                        value={testimonial.role}
                        onChange={(e) => updateTestimonial(index, 'role', e.target.value)}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Örn: Daire Sahibi"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-white/70 mb-2">Yorum</label>
                    <textarea
                      value={testimonial.comment}
                      onChange={(e) => updateTestimonial(index, 'comment', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                      placeholder="Kullanıcı yorumu..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Puan</label>
                    <div className="flex space-x-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => updateTestimonial(index, 'rating', star)}
                          className="cursor-pointer"
                        >
                          <i className={`${star <= testimonial.rating ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-white/30'} text-2xl`}></i>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {testimonials.length === 0 && (
                <div className="text-center py-12 text-white/60">
                  <i className="ri-chat-quote-line text-6xl mb-4"></i>
                  <p>Henüz kullanıcı deneyimi eklenmemiş</p>
                  <p className="text-sm">Yeni ekle butonuna tıklayarak başlayın</p>
                </div>
              )}
            </div>
          )}

          {/* Users Management Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-white mb-2">Kullanıcı Yönetimi</h3>
                <p className="text-white/60 text-sm">Toplam {users.length} kullanıcı • Kullanıcıların admin yetkilerini buradan yönetebilirsiniz</p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-white/70 font-medium text-sm">İsim</th>
                      <th className="text-left py-3 px-4 text-white/70 font-medium text-sm">E-posta</th>
                      <th className="text-left py-3 px-4 text-white/70 font-medium text-sm">Telefon</th>
                      <th className="text-left py-3 px-4 text-white/70 font-medium text-sm">Kayıt Tarihi</th>
                      <th className="text-center py-3 px-4 text-white/70 font-medium text-sm">Admin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((userProfile) => (
                      <tr key={userProfile.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-3 px-4 text-white">
                          {userProfile.full_name || <span className="text-white/40 italic">Belirtilmemiş</span>}
                        </td>
                        <td className="py-3 px-4 text-white/80">{userProfile.email || '-'}</td>
                        <td className="py-3 px-4 text-white/80">
                          {userProfile.phone || <span className="text-white/40 italic">Belirtilmemiş</span>}
                        </td>
                        <td className="py-3 px-4 text-white/80">
                          {userProfile.created_at ? new Date(userProfile.created_at).toLocaleDateString('tr-TR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => toggleUserAdmin(userProfile.id, userProfile.is_admin)}
                            disabled={userProfile.id === user?.id || togglingUserId === userProfile.id}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                              userProfile.is_admin
                                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                : 'bg-white/10 text-white/70 hover:bg-white/20'
                            } ${(userProfile.id === user?.id || togglingUserId === userProfile.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {togglingUserId === userProfile.id ? (
                              <>
                                <i className="ri-loader-4-line animate-spin mr-1"></i>
                                Güncelleniyor...
                              </>
                            ) : userProfile.is_admin ? (
                              <>
                                <i className="ri-shield-check-line mr-1"></i>
                                Admin
                              </>
                            ) : (
                              <>
                                <i className="ri-user-line mr-1"></i>
                                Kullanıcı
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {users.length === 0 && (
                  <div className="text-center py-12 text-white/60">
                    <i className="ri-user-line text-6xl mb-4"></i>
                    <p>Henüz kullanıcı bulunmuyor</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Save Button - Only show for content tabs */}
        {activeTab !== 'users' && (
          <div className="mt-8 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-8 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-2"></i>
                  Değişiklikleri Kaydet
                </>
              )}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
